<template>
    <div class="bg-light min-vh-100">
        <main class="container py-4">
            <div class="row justify-content-center">
                <div class="col-lg-10">
                    <div class="card shadow-sm">
                        <div class="card-body">
                            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <h1 class="h4 mb-0">Reporting</h1>
                                <button
                                    class="btn btn-outline-secondary btn-sm"
                                    type="button"
                                    @click="fetchAll"
                                    :disabled="loading"
                                >
                                    {{ loading ? "Refreshing..." : "Refresh" }}
                                </button>
                            </div>

                            <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>

                            <!-- Date range picker -->
                            <div class="row g-2 mb-3">
                                <div class="col-md-3">
                                    <label class="form-label small mb-1">From</label>
                                    <input v-model="dateFrom" type="date" class="form-control form-control-sm" />
                                </div>
                                <div class="col-md-3">
                                    <label class="form-label small mb-1">To</label>
                                    <input v-model="dateTo" type="date" class="form-control form-control-sm" />
                                </div>
                                <div class="col-md-2 d-flex align-items-end">
                                    <button class="btn btn-primary btn-sm" @click="fetchEnhanced" :disabled="loading">
                                        Apply
                                    </button>
                                </div>
                            </div>

                            <!-- Nav tabs -->
                            <ul class="nav nav-tabs mb-3">
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'overview' }" href="#" @click.prevent="activeTab = 'overview'">Overview</a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'productivity' }" href="#" @click.prevent="activeTab = 'productivity'">Technician Productivity</a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'issues' }" href="#" @click.prevent="activeTab = 'issues'">Issue Analytics</a>
                                </li>
                                <li class="nav-item" v-if="isSupervisor">
                                    <a class="nav-link" :class="{ active: activeTab === 'revenue' }" href="#" @click.prevent="activeTab = 'revenue'">Revenue</a>
                                </li>
                                <li class="nav-item" v-if="isSupervisor">
                                    <a class="nav-link" :class="{ active: activeTab === 'margins' }" href="#" @click.prevent="activeTab = 'margins'">Margins</a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'devices' }" href="#" @click.prevent="activeTab = 'devices'">Device Types</a>
                                </li>
                            </ul>

                            <div v-if="loading && !error" class="text-muted">Loading reports...</div>

                            <div v-else>
                                <!-- Overview Tab -->
                                <div v-if="activeTab === 'overview'">
                                    <section class="mb-4">
                                        <h2 class="h6 mb-2">Devices by Status</h2>
                                        <div v-if="byStatus.length === 0" class="text-muted small">No data available.</div>
                                        <div v-else class="table-responsive">
                                            <table class="table table-sm table-bordered align-middle mb-0">
                                                <thead class="table-light">
                                                    <tr><th>Status</th><th class="text-end">Count</th></tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="row in byStatus" :key="row.order_status">
                                                        <td>{{ row.order_status_display || row.order_status }}</td>
                                                        <td class="text-end">{{ row.count }}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </section>

                                    <section class="mb-4">
                                        <h2 class="h6 mb-2">Devices by Assignee</h2>
                                        <div v-if="byAssignee.length === 0" class="text-muted small">No data available.</div>
                                        <div v-else class="table-responsive">
                                            <table class="table table-sm table-bordered align-middle mb-0">
                                                <thead class="table-light">
                                                    <tr><th>Assignee</th><th class="text-end">Count</th></tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="row in byAssignee" :key="row.user_id || 'unassigned'">
                                                        <td>{{ row.username || "Unassigned" }}</td>
                                                        <td class="text-end">{{ row.count }}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </section>

                                    <section>
                                        <h2 class="h6 mb-2">Completion Times</h2>
                                        <div v-if="completionTimes.length === 0" class="text-muted small">No completed devices.</div>
                                        <div v-else class="table-responsive">
                                            <table class="table table-sm table-bordered align-middle mb-0">
                                                <thead class="table-light">
                                                    <tr>
                                                        <th>ID</th><th>Serial</th><th>Started</th><th>Completed</th><th class="text-end">Hours</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="row in completionTimes" :key="row.order_id">
                                                        <td>{{ row.order_id }}</td>
                                                        <td>{{ row.serial_number }}</td>
                                                        <td>{{ formatDate(row.started_at) }}</td>
                                                        <td>{{ formatDate(row.completed_at) }}</td>
                                                        <td class="text-end">{{ formatHours(row.duration_hours) }}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </section>
                                </div>

                                <!-- Productivity Tab -->
                                <div v-if="activeTab === 'productivity'">
                                    <h2 class="h6 mb-2">Technician Productivity</h2>
                                    <div v-if="!enhanced.tech_productivity || enhanced.tech_productivity.length === 0" class="text-muted small">No data.</div>
                                    <div v-else class="table-responsive">
                                        <table class="table table-sm table-bordered mb-0">
                                            <thead class="table-light">
                                                <tr>
                                                    <th>Technician</th>
                                                    <th class="text-end">Completed</th>
                                                    <th class="text-end">Avg Hours</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="row in enhanced.tech_productivity" :key="row.user_id">
                                                    <td>{{ row.username }}</td>
                                                    <td class="text-end">{{ row.completed_count }}</td>
                                                    <td class="text-end">{{ row.avg_completion_hours != null ? row.avg_completion_hours : "—" }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <!-- Issues Tab -->
                                <div v-if="activeTab === 'issues'">
                                    <h2 class="h6 mb-2">Issue Analytics</h2>
                                    <div v-if="!enhanced.issue_analytics" class="text-muted small">No data.</div>
                                    <div v-else>
                                        <p class="small" v-if="enhanced.issue_analytics.avg_resolution_hours != null">
                                            Average resolution time: <strong>{{ enhanced.issue_analytics.avg_resolution_hours }} hours</strong>
                                        </p>

                                        <h3 class="h6 mt-3 mb-2">By Severity</h3>
                                        <table class="table table-sm table-bordered mb-3" v-if="enhanced.issue_analytics.by_severity && enhanced.issue_analytics.by_severity.length">
                                            <thead class="table-light">
                                                <tr><th>Severity</th><th class="text-end">Count</th></tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="row in enhanced.issue_analytics.by_severity" :key="row.issue_severity">
                                                    <td>{{ row.issue_severity }}</td>
                                                    <td class="text-end">{{ row.count }}</td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        <h3 class="h6 mt-3 mb-2">Top Issue Descriptions</h3>
                                        <table class="table table-sm table-bordered mb-0" v-if="enhanced.issue_analytics.top_descriptions && enhanced.issue_analytics.top_descriptions.length">
                                            <thead class="table-light">
                                                <tr><th>Description</th><th class="text-end">Count</th></tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(row, i) in enhanced.issue_analytics.top_descriptions" :key="i">
                                                    <td>{{ row.issue_description }}</td>
                                                    <td class="text-end">{{ row.count }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <!-- Revenue Tab -->
                                <div v-if="activeTab === 'revenue'">
                                    <h2 class="h6 mb-2">Revenue Report</h2>
                                    <div v-if="!enhanced.revenue_report" class="text-muted small">No data.</div>
                                    <div v-else>
                                        <div class="row g-3 mb-3">
                                            <div class="col-md-4">
                                                <div class="card text-center">
                                                    <div class="card-body py-2">
                                                        <div class="h5 mb-0">${{ enhanced.revenue_report.total_sales }}</div>
                                                        <small class="text-muted">Total Sales</small>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="card text-center">
                                                    <div class="card-body py-2">
                                                        <div class="h5 mb-0">{{ enhanced.revenue_report.sold_count }}</div>
                                                        <small class="text-muted">Units Sold</small>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="card text-center">
                                                    <div class="card-body py-2">
                                                        <div class="h5 mb-0">${{ enhanced.revenue_report.avg_sale_price }}</div>
                                                        <small class="text-muted">Avg Sale Price</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <h3 class="h6 mt-3 mb-2">By Month</h3>
                                        <table class="table table-sm table-bordered mb-0" v-if="enhanced.revenue_report.by_month && enhanced.revenue_report.by_month.length">
                                            <thead class="table-light">
                                                <tr><th>Month</th><th class="text-end">Count</th><th class="text-end">Revenue</th></tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="row in enhanced.revenue_report.by_month" :key="row.month">
                                                    <td>{{ row.month }}</td>
                                                    <td class="text-end">{{ row.count }}</td>
                                                    <td class="text-end">${{ row.revenue || 0 }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <!-- Margins Tab -->
                                <div v-if="activeTab === 'margins'">
                                    <h2 class="h6 mb-2">Profit by Device Model</h2>
                                    <div v-if="!sortedMargins || sortedMargins.length === 0" class="text-muted small">No margin data available. Requires sold orders with both actual_cost and sold_price set.</div>
                                    <div v-else class="table-responsive">
                                        <table class="table table-sm table-bordered mb-0">
                                            <thead class="table-light">
                                                <tr>
                                                    <th>Device Model</th>
                                                    <th class="text-end">Units Sold</th>
                                                    <th class="text-end">Avg Cost</th>
                                                    <th class="text-end">Avg Sale</th>
                                                    <th class="text-end">Avg Margin</th>
                                                    <th class="text-end">Total Margin</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="row in sortedMargins" :key="row.device_commonname">
                                                    <td>{{ row.device_commonname }}</td>
                                                    <td class="text-end">{{ row.units }}</td>
                                                    <td class="text-end">${{ Number(row.avg_cost).toFixed(2) }}</td>
                                                    <td class="text-end">${{ Number(row.avg_revenue).toFixed(2) }}</td>
                                                    <td class="text-end" :class="row.avg_margin >= 0 ? 'text-success' : 'text-danger'">
                                                        ${{ Number(row.avg_margin).toFixed(2) }}
                                                    </td>
                                                    <td class="text-end" :class="row.total_margin >= 0 ? 'text-success' : 'text-danger'">
                                                        ${{ Number(row.total_margin).toFixed(2) }}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <!-- Device Types Tab -->
                                <div v-if="activeTab === 'devices'">
                                    <h2 class="h6 mb-2">Device Type Breakdown</h2>
                                    <div v-if="!enhanced.device_type_breakdown || enhanced.device_type_breakdown.length === 0" class="text-muted small">No data.</div>
                                    <div v-else class="table-responsive">
                                        <table class="table table-sm table-bordered mb-0">
                                            <thead class="table-light">
                                                <tr>
                                                    <th>Device Type</th>
                                                    <th class="text-end">Total</th>
                                                    <th class="text-end">Completed</th>
                                                    <th class="text-end">Completion Rate</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="row in enhanced.device_type_breakdown" :key="row.device_type">
                                                    <td>{{ row.device_type }}</td>
                                                    <td class="text-end">{{ row.total }}</td>
                                                    <td class="text-end">{{ row.completed }}</td>
                                                    <td class="text-end">{{ row.total > 0 ? Math.round((row.completed / row.total) * 100) : 0 }}%</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import { APIService } from "../http/APIService";

export default {
    name: "Reports",
    data() {
        return {
            loading: false,
            error: null,
            api: new APIService(),
            activeTab: "overview",
            dateFrom: "",
            dateTo: "",

            // Basic reporting data
            byStatus: [],
            byAssignee: [],
            completionTimes: [],
            ordersWithIssues: 0,

            // Enhanced reporting data
            enhanced: {},
        };
    },
    computed: {
        sortedMargins() {
            const data = this.enhanced.margin_by_model || [];
            return [...data].sort((a, b) => b.total_margin - a.total_margin);
        },
        isSupervisor() {
            try {
                const raw = localStorage.getItem("currentUser");
                if (!raw) return false;
                const user = JSON.parse(raw);
                const role = (user.role || "").toLowerCase();
                return role === "supervisor" || role === "admin";
            } catch {
                return false;
            }
        },
    },
    created() {
        this.fetchAll();
    },
    methods: {
        async fetchAll() {
            this.loading = true;
            this.error = null;
            try {
                await Promise.all([this.fetchBasic(), this.fetchEnhanced()]);
            } catch {
                this.error = "Failed to load reports.";
            } finally {
                this.loading = false;
            }
        },

        async fetchBasic() {
            const accessToken = localStorage.getItem("accessToken");
            const response = await this.api.getOrderReporting(accessToken);
            const data = response.data || {};
            this.byStatus = data.by_status || [];
            this.byAssignee = data.by_assignee || [];
            this.completionTimes = data.completion_times || [];
            this.ordersWithIssues = data.orders_with_issues ?? 0;
        },

        async fetchEnhanced() {
            const accessToken = localStorage.getItem("accessToken");
            const params = {};
            if (this.dateFrom) params.date_from = this.dateFrom;
            if (this.dateTo) params.date_to = this.dateTo;
            const response = await this.api.getEnhancedReporting(accessToken, params);
            this.enhanced = response.data || {};
        },

        formatDate(value) {
            if (!value) return "";
            return new Date(value).toLocaleString();
        },

        formatHours(value) {
            if (value === null || value === undefined) return "";
            return value.toFixed(1);
        },
    },
};
</script>
